import { FC } from 'react';
import { FONT_COLOURS } from '@belong/themes';
import { IPlanCard } from '@belong/types';
import { FEATURES, useFeatureToggles } from '@belong/feature-toggles';
import { Stack } from '../../ui';
import { AccordionList } from '../../lists';
import { RichText } from '../../styles/Typography';
import * as styles from './PlanCard.styles';
import { CostBreakdown } from './CostBreakdown/CostBreakdown';
import { PlanCTA } from './PlanCTA/PlanCTA';
import { SpeedBreakdown } from './SpeedBreakdown/SpeedBreakdown';
import { getAccordionRowListItems } from './utils';
import { PLAN_CARD_TEST_ID } from './testIds';
import { PlanOffersAndAlerts } from './PlanOffersAndAlerts/PlanOffersAndAlerts';
import { IMultipleAccordionPlanCardProps, IPlanCardBody } from './types';
import { ControlledHeightContainer, createRefHandler } from './ControlledHeightContainer';

export const PlanCardBodyMobile: FC<IPlanCard & IMultipleAccordionPlanCardProps & IPlanCardBody> = props => {
  const {
    planType,
    title,
    plan,
    dataBeforePromo,
    priceBeforePromo,
    typicalSpeedsCopy,
    inclusions,
    addOns,
    suitability,
    disclaimer,
    buttonLabel,
    accordionRefs,
    toggleAccordions,
    columnIndex = 0,
    accordionRowContentRefs,
    focusTextOnSelect,
    planGroupIncludesPromo,
    selectionStatus,
    buttonUrl,
    onPlanCardCtaClick,
    overrideButtonLabel = buttonLabel
  } = props;

  const { disclaimerRefs, disclaimerHeight = 'auto', removeDisclaimerHeight } = props;
  const hasDisclaimerSpacer = disclaimerHeight !== '0px';
  const forcedDisclaimerHeight = hasDisclaimerSpacer && !removeDisclaimerHeight ? disclaimerHeight : undefined;
  const { isFeatureOn } = useFeatureToggles();
  const isAccordionFeatureOn = isFeatureOn(FEATURES.MOBILE_PLANS_ACCORDION_AB_TEST);

  return (
    <styles.FullHeightStack alignX="center" divider space={{ xs: 'large', md: 'xlarge' }}>
      <CostBreakdown
        data-testid={PLAN_CARD_TEST_ID.planCostBreakDown}
        planType={planType}
        plan={plan}
        dataBeforePromo={dataBeforePromo}
        priceBeforePromo={priceBeforePromo}
        alignBottom={!priceBeforePromo && planGroupIncludesPromo} // a plan that has a promo callout will have no offest
      />
      <PlanOffersAndAlerts {...props} />
      <SpeedBreakdown
        planType={planType}
        plan={plan}
        typicalSpeedsCopy={typicalSpeedsCopy}
        data-testid={PLAN_CARD_TEST_ID.planSpeedBreakDown}
      />

      {disclaimer && (
        <ControlledHeightContainer
          data-testid="disclaimer"
          height={forcedDisclaimerHeight}
          ref={createRefHandler(columnIndex, disclaimerRefs)}
        >
          <RichText
            {...disclaimer}
            hasColor={isAccordionFeatureOn ? FONT_COLOURS.STRONG : FONT_COLOURS.FINE_PRINT}
            p="copySmall"
            data-testid={PLAN_CARD_TEST_ID.planDisclaimer}
            alignment={isAccordionFeatureOn ? 'left' : 'center'}
          />
        </ControlledHeightContainer>
      )}
      <styles.FullHeightStack alignX="center" space={{ xs: 'small', md: 'medium' }} fillLastItem>
        <Stack alignX="center" space={{ xs: 'xxsmall', md: 'xsmall' }}>
          <Stack alignX="center">
            {inclusions && (
              <styles.AccordionsWrapper>
                <AccordionList
                  data-testid={PLAN_CARD_TEST_ID.planInclusions}
                  items={getAccordionRowListItems({
                    accordionRowList: inclusions,
                    accordionRefs,
                    columnIndex,
                    toggleAccordions,
                    accordionRowContentRefs
                  })}
                  variant="slim"
                  roundedCorners
                />
              </styles.AccordionsWrapper>
            )}
          </Stack>
          <Stack alignX="center">
            <styles.AccordionsWrapper>
              {addOns && (
                <AccordionList
                  data-testid={PLAN_CARD_TEST_ID.planAddons}
                  items={getAccordionRowListItems({
                    accordionRowList: addOns,
                    accordionRefs,
                    columnIndex,
                    toggleAccordions,
                    startIndex: inclusions?.length,
                    accordionRowContentRefs
                  })}
                  variant="slim"
                />
              )}
              {suitability && (
                <AccordionList
                  data-testid={PLAN_CARD_TEST_ID.planSuitability}
                  items={getAccordionRowListItems({
                    accordionRowList: suitability,
                    accordionRefs,
                    columnIndex,
                    toggleAccordions,
                    startIndex: (inclusions?.length ?? 0) + (addOns?.length ?? 0),
                    accordionRowContentRefs
                  })}
                  variant="slim"
                />
              )}
            </styles.AccordionsWrapper>
          </Stack>
        </Stack>

        <PlanCTA
          label={overrideButtonLabel}
          selectionStatus={selectionStatus}
          url={buttonUrl}
          onClick={onPlanCardCtaClick}
          className="cta-button"
          focusTextOnSelect={focusTextOnSelect}
          planName={title}
          data-testid={PLAN_CARD_TEST_ID.planCTAWrapper}
        />
      </styles.FullHeightStack>
    </styles.FullHeightStack>
  );
};

PlanCardBodyMobile.displayName = 'PlanCardBodyMobile';
